/**
 * Landing Page — admeru
 *
 * Built from the "Admeru Landing Page" design canvas (Desktop 1440 / Mobile 390 boards).
 * One responsive tree: layout lives in the scoped `lp-*` classes at the bottom of the file,
 * so the page stays light whatever theme the signed-in app uses.
 *
 * Positioning: Admeru learns a business, writes its strategy, plans each month and makes the
 * campaigns and posts; the owner (or their agency) approves and it launches. Ads can also be
 * made any time, outside the plan.
 *
 * Data:
 *  - Prices and plan limits come from the pricing catalog (core/backend/pricing_catalog.py),
 *    shown in one currency picked by the visitor's location (/api/v1/geo-pricing).
 *  - Ads are real Ad Studio outputs in /public/images/landing/ads (web-sized copies).
 *    Do not use showcase/fragrance-out.png or showcase/footwear-in.png: they carry stock
 *    watermarks.
 *  - Product panels (plan, production, review, results, leads) are drawn here with example
 *    data for a sample business and labelled as examples.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '@/components/ui/Logo';
import { usePricingCatalog, type CatalogPlan } from '@/hooks/usePricingCatalog';
import { apiClient } from '@/services/api';

// ── Config ──────────────────────────────────────────────────────────────────

const TRIAL = '/register?plan=pro'; // only this plan starts the 14-day trial without a card
const LOGIN = '/login';
const MAIL_SALES = 'mailto:sales@admeru.com?subject=Agency%20and%20enterprise%20plans';
const MAIL_HELLO = 'mailto:connect@admeru.com';

const AD = (name: string) => `/images/landing/ads/${name}.jpg`;

/** Channels switched on in production (core/backend/platform_availability.py DEFAULT_ON). */
const LIVE_CHANNELS: Record<string, string> = {
  meta: 'Facebook and Instagram',
  google_ads: 'Google Ads',
  pinterest: 'Pinterest',
};

const LANDING_TIERS = ['basic', 'growth', 'pro'];

// ── Small parts ──────────────────────────────────────────────────────────────

const Check: React.FC<{ color?: string; size?: number }> = ({ color = '#1F8A5B', size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.6} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ flex: `0 0 ${size}px` }}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const Arrow: React.FC<{ size?: number; color?: string }> = ({ size = 16, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M5 12h14" /><path d="M13 6l6 6-6 6" />
  </svg>
);

const Chevron = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M3 4.5l3 3 3-3" /></svg>
);

/** Small channel marks in their official colours (Meta and Pinterest allow their logos to show an
 *  integration). Google Ads stays text until Google's permission for its logo. */
const ChannelMark: React.FC<{ name: 'facebook' | 'instagram' | 'pinterest' }> = ({ name }) => (
  <svg width="18" height="18" viewBox="0 0 28 28" aria-hidden="true" className="lp-ch-mark">
    {name === 'instagram' && <defs><linearGradient id="lp-ig-grad" x1="0" y1="28" x2="28" y2="0" gradientUnits="userSpaceOnUse"><stop offset="0%" stopColor="#F09433" /><stop offset="30%" stopColor="#E6683C" /><stop offset="55%" stopColor="#DC2743" /><stop offset="75%" stopColor="#CC2366" /><stop offset="100%" stopColor="#BC1888" /></linearGradient></defs>}
    <rect width="28" height="28" rx="7" fill={name === 'facebook' ? '#1877F2' : name === 'pinterest' ? '#E60023' : 'url(#lp-ig-grad)'} />
    {name === 'facebook' && <path d="M18.5 5H16C13.5 5 12 6.5 12 9v2H9.5v3.5H12V22h3.5v-7.5H18l.5-3.5H15.5V9c0-.8.4-1.5 1.5-1.5H18.5V5z" fill="#FFFFFF" />}
    {name === 'instagram' && <><rect x="8" y="8" width="12" height="12" rx="4" stroke="#FFFFFF" strokeWidth="1.9" fill="none" /><circle cx="14" cy="14" r="3" stroke="#FFFFFF" strokeWidth="1.9" fill="none" /><circle cx="20" cy="8" r="1.3" fill="#FFFFFF" /></>}
    {name === 'pinterest' && <path d="M14 3C8 3 3 8 3 14c0 4.6 2.8 8.5 6.8 10.1-.1-.9-.2-2.3.1-3.3l1.4-5.8s-.3-.7-.3-1.8c0-1.7 1-3 2.2-3 1 0 1.5.8 1.5 1.7 0 1-.7 2.5-1 3.8-.3 1.1.5 2 1.7 2 2 0 3.4-2.6 3.4-5.7 0-2.4-1.7-4-4.2-4-2.9 0-4.5 2.1-4.5 4.3 0 .8.3 1.7.7 2.2.1.1.1.2 0 .3l-.6 2.5c-.1.2-.2.3-.4.2C7.1 17.8 6 15.4 6 13c0-3.8 2.8-7.3 8-7.3 4.2 0 7.5 3 7.5 7 0 4.1-2.6 7.4-6.2 7.4-1.2 0-2.4-.6-2.8-1.4l-.7 2.8c-.3.9-.9 2.1-1.4 2.8C11.2 24.4 12.6 25 14 25c6 0 11-5 11-11S20 3 14 3z" fill="#FFFFFF" transform="translate(14 14) scale(0.72) translate(-14 -14)" />}
  </svg>
);

const Eyebrow: React.FC<{ children: React.ReactNode; onNavy?: boolean }> = ({ children, onNavy }) => (
  <div className={onNavy ? 'lp-eyebrow lp-eyebrow-navy' : 'lp-eyebrow'}>{children}</div>
);

const Pill: React.FC<{ tone: 'ok' | 'brand' | 'info' | 'warn' | 'grey'; children: React.ReactNode }> = ({ tone, children }) => (
  <span className={`lp-pill lp-pill-${tone}`}>{children}</span>
);

/** A drawn ad tile for the sample clinic, used where a real capture of that campaign would go. */
const ClinicAd: React.FC<{ w: number; h: number; tone?: 0 | 1 | 2; line?: string }> = ({ w, h, tone = 0, line = 'Year-end check-up' }) => {
  const bg = ['#F6E7D8', '#0F1526', '#FDEBDD'][tone];
  const fg = tone === 1 ? '#FFFFFF' : '#12141C';
  return (
    <div className="lp-clinic-ad" style={{ width: w, height: h, background: bg, color: fg }}>
      <span className="lp-clinic-ad-tag" style={{ color: tone === 1 ? '#FFB27A' : '#B4530F' }}>Saffron Smiles</span>
      <span className="lp-clinic-ad-line">{line}</span>
      <span className="lp-clinic-ad-cta" style={{ background: tone === 1 ? '#F27C28' : '#0F1526', color: tone === 1 ? '#0F1526' : '#FFFFFF' }}>Book now</span>
    </div>
  );
};

// ── Pricing ──────────────────────────────────────────────────────────────────

type Currency = 'INR' | 'USD';

function useGeoCurrency(): Currency {
  const [currency, setCurrency] = useState<Currency>('USD');
  useEffect(() => {
    let live = true;
    apiClient
      .get('/api/v1/geo-pricing')
      .then((r) => { if (live && (r.data?.currency === 'INR' || r.data?.currency === 'USD')) setCurrency(r.data.currency); })
      .catch(() => { /* keep USD */ });
    return () => { live = false; };
  }, []);
  return currency;
}

const money = (minor: number | null | undefined, currency: Currency) => {
  if (minor == null) return '';
  const major = Math.round(minor / 100);
  return currency === 'INR' ? `₹${major.toLocaleString('en-IN')}` : `$${major.toLocaleString('en-US')}`;
};

function planBullets(p: CatalogPlan): string[] {
  const lim = p.limits as Record<string, number | string[] | boolean>;
  const orgs = Number(lim.organizations_per_user ?? 1);
  const team = Number(lim.team_members_per_organization ?? 1);
  const days = Number(lim.analytics_retention_days ?? 0);
  const exports = (lim.analytics_export_formats as string[] | undefined) ?? [];
  const channels = p.platforms.filter((c) => LIVE_CHANNELS[c]).map((c) => LIVE_CHANNELS[c]);
  const out: string[] = [];
  const biz = p.features.client_workspaces ? `${orgs} client businesses` : orgs === 1 ? '1 business' : `${orgs} businesses`;
  out.push(team > 1 ? `${biz}, ${team} team members each` : biz);
  out.push(`${p.monthly_credits.toLocaleString('en-US')} credits a month`);
  if (channels.length) out.push(channels.length > 1 ? `${channels.slice(0, -1).join(', ')} and ${channels[channels.length - 1]}` : channels[0]);
  out.push('Strategy, monthly plan and approvals');
  if (p.features.image_generation_premium) out.push('Premium images');
  if (p.features.priority_support) out.push('Priority support');
  if (days > 0) out.push(`Results for ${days} days${exports.includes('csv') ? ', CSV export' : ''}`);
  return out;
}

// ── Content ──────────────────────────────────────────────────────────────────

const LOOP = [
  { t: 'Understand', d: 'Facts about your business, each with its source' },
  { t: 'Strategy', d: 'Who, where, what to say, with reasons' },
  { t: 'Monthly plan', d: 'Campaigns and posts on dates, budget by channel' },
  { t: 'Make', d: 'Ads in every size, video and posts' },
  { t: 'You approve', d: 'Nothing goes out without your OK', hl: 'brand' },
  { t: 'Launch', d: 'On its dates, to your channels' },
  { t: 'Results', d: 'Spend, results and a leads inbox', hl: 'navy' },
];

// Language section. Ads are real Ad Studio outputs kept in model_lab/showcase/landing.
const LANG_BG = (name: string) => `/images/landing/ads/lang/bg/${name}.jpg`;
/** Every ad on this page's Ad Studio section: the Ad Studio showcase and the language ads made in
 *  model_lab (showcase/landing), drifting behind the message in six columns, mixed so each column
 *  has both. */
const LANG_COLUMNS = [
  ['beverage-out', 'gu-navratri-chaniya', 'saas-reporting-out', 'ar-ramadan-oud', 'chai-hi', 'coffee-out'],
  ['ko-wireless-earbuds', 'jewelry-diwali-out', 'te-avakaya-pickle', 'cosmetics-out', 'it-amalfi-boat-tours'],
  ['audio-out', 'ja-skincare-device', 'pizza-out', 'hinglish-upi-cashback', 'de-commuter-ebike', 'chai-bn'],
  ['ta-filter-coffee-auto', 'watch-promo-out', 'mr-ganeshotsav-modak', 'es-running-shoes', 'pt-acai-bowls'],
  ['ethnic-wear-out', 'fr-plant-based-protein', 'ml-hair-oil', 'travel-out', 'ja-uji-matcha', 'chai-en'],
  ['tr-sustainable-fashion', 'gourmet-out', 'pa-highway-dhaba', 'fr-galette-des-rois', 'beverage-out'],
];

/** A few of the 48 languages in Ad Studio's picker, in their own scripts. */
const LANG_SAMPLE = ['हिन्दी', 'தமிழ்', 'বাংলা', 'Español', 'العربية', '日本語', 'Deutsch', '한국어'];
/** Noto faces for just the glyphs of LANG_SAMPLE (Google Fonts `text=` subset: a few KB). */
const LANG_FONTS = 'https://fonts.googleapis.com/css2?family=Noto+Sans+Devanagari:wght@500&family=Noto+Sans+Tamil:wght@500'
  + '&family=Noto+Sans+Bengali:wght@500&family=Noto+Sans+Arabic:wght@500&family=Noto+Sans+JP:wght@500&family=Noto+Sans+KR:wght@500'
  + `&text=${encodeURIComponent(LANG_SAMPLE.join(''))}&display=swap`;

/** Who it's for: one line from a single business to an agency. */
const SCALE = [
  { t: 'One business', d: 'A shop, a clinic, a studio. Its owner approves.', icon: 'one' },
  { t: 'A chain or a brand family', d: 'Many locations or brands, one plan each.', icon: 'many' },
  { t: 'An in-house team', d: 'Roles for who makes and who approves.', icon: 'team' },
  { t: 'An agency or freelancer', d: 'Every client in one place, one bill.', icon: 'agency', id: 'agencies' },
];

const FAQ: [string, string][] = [
  ['How does the free trial work?', 'You get 14 days with everything switched on, for your own business. No credit card is needed. At the end, choose a plan to keep going, or stay on the free plan.'],
  ['Do I need marketing experience?', 'No. Admeru writes the strategy and the plan in plain language. You check that the facts about your business are right and approve what it makes.'],
  ['What exactly needs my approval? Can anything go live without me?', 'The strategy, each month’s plan, every ad and every launch wait for your OK. If a date is missed, Admeru flags it and nothing is published late without you.'],
  ['Do I have to wait for the monthly plan to run an ad?', 'No. Start a campaign in a chat or make an ad in Ad Studio whenever you need one. It uses your strategy and brand, and still waits for your OK.'],
  ['I run an agency or freelance. Can I use it for clients?', 'Yes, and that is a core use. Each client is its own business with its own strategy, plan and ad accounts. Admeru does the groundwork; your team reviews, edits and approves.'],
  ['Is Admeru like having a CMO?', 'It does the parts of a marketing head’s job that drive paid and social growth: it learns the business, sets the strategy, plans each month and gets the work made. It does not cover SEO, email or competitor research.'],
  ['Which languages can my ads be in?', '48 languages and Hinglish: 23 Indian languages, from Hindi, Bengali and Tamil to Santali and Manipuri, plus English and 24 global languages including Spanish, Arabic, Japanese and Chinese. Type your idea in any of them or pick one. The words are written natively, not translated word for word, and set in a typeface made for that script.'],
  ['Which channels are supported?', 'Facebook and Instagram, Google Ads and Pinterest.'],
  ['Do I need my own ad accounts?', 'Yes. Ad spend is paid to the platforms from your own accounts. Admeru charges for the service.'],
  ['What are credits and what uses them?', 'Credits pay for what gets made: ads, video and posts. You see an estimate before production starts, and Admeru stops and asks if a job would run over.'],
  ['Who owns the ads?', 'You do. Everything you create is yours to use commercially, on any channel, during and after your subscription.'],
  ['How is my data handled?', 'Assets and leads are encrypted and not used to train public models. See the DPA, sub-processor list and data deletion pages.'],
];

// ══════════════════════════════════════════════════════════════════════════════

export const LandingPage: React.FC = () => {
  const [menu, setMenu] = useState(false);
  const [faq, setFaq] = useState(0);
  const currency = useGeoCurrency();
  const catalog = usePricingCatalog();
  const plans = (catalog?.plans ?? []).filter((p) => LANDING_TIERS.includes(p.name));
  const enterprise = catalog?.plans.find((p) => p.name === 'enterprise');
  // Background ads load only when the visitor gets near the language section.
  const langRef = useRef<HTMLElement>(null);
  const [langNear, setLangNear] = useState(false);
  useEffect(() => {
    const el = langRef.current;
    if (!el || !('IntersectionObserver' in window)) { setLangNear(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setLangNear(true); io.disconnect(); } }, { rootMargin: '800px 0px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  // The October chart draws itself when it scrolls into view.
  const monthRef = useRef<HTMLElement>(null);
  const [monthGo, setMonthGo] = useState(false);
  useEffect(() => {
    const el = monthRef.current;
    if (!el || !('IntersectionObserver' in window)) { setMonthGo(true); return; }
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setMonthGo(true); io.disconnect(); } }, { threshold: 0.35 });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  const langFaq = FAQ.findIndex(([q]) => q.startsWith('Which languages'));
  const openLanguages = (e: React.MouseEvent) => {
    e.preventDefault();
    setFaq(langFaq);
    document.getElementById('faq')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="lp">
      <style>{styles}</style>

      {/* ── NAV ─────────────────────────────────────────────────────────── */}
      <header className="lp-header">
        <div className="lp-wrap lp-nav">
          <Logo height={26} onDark={false} to="/" />
          <nav aria-label="Main" className="lp-nav-links">
            <a href="#how" className="lp-nav-link">Product <Chevron /></a>
            <a href="#who" className="lp-nav-link">Solutions <Chevron /></a>
            <a href="#pricing" className="lp-nav-link">Pricing</a>
            <a href="#trust" className="lp-nav-link">Security</a>
          </nav>
          <Link to={LOGIN} className="lp-nav-signin">Sign in</Link>
          <Link to={TRIAL} className="lp-btn lp-btn-primary lp-btn-sm">Start free trial</Link>
          <button type="button" className="lp-menu-btn" aria-label={menu ? 'Close menu' : 'Open menu'} aria-expanded={menu} onClick={() => setMenu((m) => !m)}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M3 6h14M3 10h14M3 14h14" /></svg>
          </button>
        </div>
        {menu && (
          <div className="lp-drawer">
            <div className="lp-drawer-h">Product</div>
            <a href="#how" onClick={() => setMenu(false)}>How it works<span>Strategy, plan, production, launch</span></a>
            <a href="#studio" onClick={() => setMenu(false)}>Ad Studio<span>Finished ads in 48 languages</span></a>
            <a href="#results" onClick={() => setMenu(false)}>After launch<span>Weekly checks, every lead in one place</span></a>
            <div className="lp-drawer-h">Solutions</div>
            <a href="#who" onClick={() => setMenu(false)}>Who it's for<span>One business, a chain, a team or an agency</span></a>
            <a href="#pricing" onClick={() => setMenu(false)}>Pricing</a>
            <a href="#trust" onClick={() => setMenu(false)}>Security</a>
            <Link to={LOGIN} className="lp-btn lp-btn-ghost" style={{ marginTop: 16 }}>Sign in</Link>
          </div>
        )}
      </header>

      {/* ── HERO ────────────────────────────────────────────────────────── */}
      <section id="top" className="lp-hero">
        <div className="lp-wrap lp-hero-grid">
          <div className="lp-hero-copy">
            <Eyebrow>Your AI marketing head</Eyebrow>
            <h1 className="lp-h1">Strategy, plans and finished ads. <span className="lp-orange">Every month, ready for your OK.</span></h1>
            <p className="lp-lead">Admeru learns the business, writes the strategy, plans each month and makes the campaigns and posts, for your own brands or your clients', in 48 languages. You approve, it launches.</p>
            <div className="lp-row">
              <Link to={TRIAL} className="lp-btn lp-btn-primary lp-btn-lg">Start 14-day free trial</Link>
              <a href="#example" className="lp-btn lp-btn-ghost lp-btn-lg">See an example month <Arrow /></a>
            </div>
            <span className="lp-note"><Check /> 14 days free. No credit card.</span>
            <span className="lp-channels"><span className="lp-channels-label">Plans and launches on</span>
              <span className="lp-ch"><ChannelMark name="facebook" />Facebook</span>
              <span className="lp-ch"><ChannelMark name="instagram" />Instagram</span>
              <span className="lp-ch">Google Ads</span>
              <span className="lp-ch"><ChannelMark name="pinterest" />Pinterest</span>
            </span>
          </div>

          <div className="lp-hero-stage">
            <div className="lp-hero-art">
              <div className="lp-hero-wash" />
              <div className="lp-hero-phone"><img src={AD('beverage-out')} alt="Finished Story ad for matcha, made by Admeru" /></div>
              <div className="lp-hero-feed">
                <div className="lp-hero-feed-head"><span className="lp-avatar" /><div>Your brand<small>Sponsored</small></div></div>
                <img src={AD('cosmetics-out')} alt="Finished feed ad for lipstick, made by Admeru" />
                <div className="lp-hero-feed-cta">Shop now<span>›</span></div>
              </div>
              <div className="lp-hero-square"><img src={AD('pizza-out')} alt="Finished square ad for a pizzeria, made by Admeru" /></div>
              <div className="lp-hero-chips">
                <div className="lp-chip lp-chip-1"><span className="lp-chip-ok"><Check size={12} /></span>Strategy approved<em>12 facts</em></div>
                <div className="lp-chip lp-chip-2"><span className="lp-chip-ok"><Check size={12} /></span>November plan<em>4 campaigns · 12 posts</em></div>
                <div className="lp-chip lp-chip-dark lp-chip-3">3 ads ready for review<b>Approve</b></div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* ── LOOP ────────────────────────────────────────────────────────── */}
      <section id="how" className="lp-section">
        <div className="lp-wrap lp-stack">
          <div className="lp-head">
            <Eyebrow>How it works</Eyebrow>
            <h2 className="lp-h2">A plan every month. Ads whenever you need them.</h2>
            <p className="lp-sub">Understand, plan, make, approve, launch, learn. Then again, a little smarter. Admeru does the groundwork; your team makes the calls. And when something can't wait for the plan, start a campaign or an ad any time.</p>
          </div>
          <div>
            <ol className="lp-loop">
              {LOOP.map((s, i) => (
                <li key={s.t} className={`lp-loop-step${s.hl ? ` lp-loop-${s.hl}` : ''}`}>
                  <span className="lp-loop-n">{String(i + 1).padStart(2, '0')}</span>
                  <b>{s.t}</b>
                  <span>{s.d}</span>
                </li>
              ))}
            </ol>
            <div className="lp-loop-back" />
            <div className="lp-loop-back-label">
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M13 8a5 5 0 1 1-1.5-3.5" /><path d="M13 2.5V5h-2.5" /></svg>
              Results become facts for next month's plan
            </div>
          </div>
          <div className="lp-anytime">
            <div className="lp-anytime-copy">
              <Eyebrow>Any time, outside the plan</Eyebrow>
              <div className="lp-h4">A launch, a sale, a slow week? Start now.</div>
              <p className="lp-small">It still uses your strategy and brand, and still waits for your OK.</p>
            </div>
            <div className="lp-card lp-anytime-card">
              <span className="lp-icon lp-icon-brand"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /></svg></span>
              <div><b>Start a campaign in a chat</b><p>Say what you want to promote. Admeru sets up the campaign, makes the ads and brings it to you to approve and launch.</p></div>
            </div>
            <div className="lp-card lp-anytime-card">
              <span className="lp-icon lp-icon-info"><svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="M21 15l-5-5-6 6" /></svg></span>
              <div><b>Make an ad in Ad Studio</b><p>One brief, several finished directions, every size. Publish it, schedule it or download it.</p></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── UNDERSTAND ──────────────────────────────────────────────────── */}
      <section className="lp-section lp-white">
        <div className="lp-wrap lp-split">
          <div className="lp-head">
            <Eyebrow>01 · Understand</Eyebrow>
            <h2 className="lp-h2">It starts by learning your business</h2>
            <p className="lp-sub">Admeru reads your website, your past ads and what competitors are doing, then builds a profile: offers, customers, markets, voice and key dates. You confirm what's right. Only confirmed facts shape your strategy.</p>
            <div className="lp-url"><span>saffronsmiles.example</span><b>Read my site</b></div>
          </div>
          <div className="lp-panel">
            <div className="lp-panel-title">Your business <span className="lp-tag">Example</span></div>
            <div className="lp-fact"><div><b>Dental chain with 14 clinics across Hyderabad, Bengaluru, Pune and Chennai</b><small>Source: <strong>Your website</strong> · About page</small></div><Pill tone="ok">Confirmed</Pill></div>
            <div className="lp-fact"><div><b>Busiest months: Diwali and December holidays</b><small>Source: <strong>You</strong></small></div><Pill tone="ok">Confirmed</Pill></div>
            <div className="lp-fact"><div><b>Two nearby chains advertise a free first check-up</b><small>Source: <strong>Competitor research</strong></small></div><Pill tone="info">New</Pill></div>
            <div className="lp-fact lp-fact-check">
              <div className="lp-fact-row"><div><b>Carousel ads brought the most leads last quarter</b><small>Source: <strong>Past results</strong> · Meta</small></div><Pill tone="warn">Check this</Pill></div>
              <div className="lp-row lp-row-tight"><span className="lp-mini-btn">Edit</span><span className="lp-mini-btn lp-mini-btn-dark">Confirm</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STRATEGY + PLAN ─────────────────────────────────────────────── */}
      <section id="example" className="lp-section">
        <div className="lp-wrap lp-stack">
          <div className="lp-head">
            <Eyebrow>02 · Strategy and plan</Eyebrow>
            <h2 className="lp-h2">A strategy you can read in a minute. A plan for every month.</h2>
            <p className="lp-sub">Who to reach, where, what to say and what the budget buys, each with the reason behind it. Then every month on a calendar: campaigns and posts on their dates, planned around your festivals, sales and seasons.</p>
          </div>
          <div className="lp-strategy">
            <div className="lp-glance">
              <Eyebrow onNavy>At a glance</Eyebrow>
              {[['Who', 'Parents 28–45 near each of the 14 clinics'], ['Where', 'Instagram, Facebook, Google search'], ['What to say', 'Gentle care for the whole family'], ['Budget buys', 'Steady bookings, more around Diwali'], ['Voice', 'Warm, plain, reassuring']].map(([k, v]) => (
                <div key={k}><b>{k}</b>{v}</div>
              ))}
            </div>
            <div className="lp-card lp-why">
              <div className="lp-row"><span className="lp-h5">Where to advertise</span><Pill tone="ok">Confidence: high</Pill></div>
              <p>Instagram and Facebook for reach among local parents; Google search for people already looking for a dentist.</p>
              <div className="lp-why-box"><b>Why:</b> most of your bookings come from families nearby, and your past carousel ads on Instagram brought the most leads.</div>
              <div className="lp-row lp-row-tight lp-small"><span>Based on</span><span className="lp-ref">14 clinics, 4 cities</span><span className="lp-ref">Carousel ads, past results</span></div>
              <div className="lp-row lp-phone-hide"><span className="lp-mini-btn">Edit section</span><span className="lp-mini-btn">Ask for changes</span><span className="lp-mini-btn lp-mini-btn-brand">Approve strategy</span></div>
            </div>
          </div>
          <div className="lp-card lp-cal-card">
            <div className="lp-cal">
              <div className="lp-cal-top"><span className="lp-h5">November 2026</span><span className="lp-muted">Move a date and the approval stays.</span><span className="lp-tag" style={{ marginLeft: 'auto' }}>Example data</span></div>
              <div className="lp-cal-days">{Array.from({ length: 30 }, (_, i) => <span key={i} className={i === 7 || i === 26 ? 'lp-cal-key' : ''}>{i + 1}</span>)}</div>
              <div className="lp-cal-board">
                <div className="lp-cal-mark" style={{ left: '24.2%' }}><span>Diwali</span></div>
                <div className="lp-cal-mark" style={{ left: '87.5%' }}><span>Black Friday</span></div>
                <div className="lp-cal-bar" style={{ left: '0.5%', width: '29%', top: 38, background: '#0F1526', color: '#FFFFFF' }}>Diwali smile offer · Meta</div>
                <div className="lp-cal-bar" style={{ left: '7%', width: '92.5%', top: 82, background: '#F27C28', color: '#0F1526' }}>Teeth whitening search · Google Ads</div>
                <div className="lp-cal-bar" style={{ left: '63.5%', width: '36%', top: 126, background: '#FDEBDD', color: '#8A3E0A', border: '1px solid #F27C28' }}>Year-end check-up reminder · Meta</div>
                {[5, 22, 38, 55, 72, 90].map((x) => <span key={x} className="lp-cal-dot" style={{ left: `${x}%` }} />)}
              </div>
              <div className="lp-row lp-small lp-muted"><span className="lp-legend"><i style={{ width: 18, height: 10, borderRadius: 3, background: '#0F1526' }} />Campaign</span><span className="lp-legend"><i style={{ width: 10, height: 10, borderRadius: 99, background: '#646D80' }} />Instagram post</span><span className="lp-legend"><i style={{ width: 0, height: 12, borderLeft: '2px dashed #F27C28' }} />Key date</span></div>
            </div>
          </div>
        </div>
      </section>

      {/* ── PRODUCTION + APPROVAL ───────────────────────────────────────── */}
      <section className="lp-section lp-white">
        <div className="lp-wrap lp-stack">
          <div className="lp-head-split">
            <div className="lp-head">
              <Eyebrow>03 · Production and approval</Eyebrow>
              <h2 className="lp-h2">The work gets made. Nothing goes live without you.</h2>
            </div>
            <p className="lp-sub">Approve the plan and Admeru makes ads in every size, 9:16 video and posts with captions, using your own photos where you have them. Each one waits for your review, at its real size, before it launches.</p>
          </div>
          <div className="lp-board">
            <div className="lp-col"><div className="lp-col-h"><span>Being made</span><span>2</span></div>
              <div className="lp-job"><b>Instagram post · Brushing tips for kids</b><div className="lp-progress" style={{ width: '62%' }} /><small>Writing caption</small></div>
              <div className="lp-job"><b>9:16 video · Diwali offer</b><div className="lp-progress" style={{ width: '30%' }} /><small>Rendering scenes</small></div>
            </div>
            <div className="lp-col"><div className="lp-col-h"><span>Ready for review</span><span>1</span></div>
              <div className="lp-job"><Pill tone="brand">Ready for review</Pill><b>Year-end check-up reminder · 4 ads</b>
                <div className="lp-row lp-row-tight" style={{ alignItems: 'flex-end' }}><ClinicAd w={48} h={48} line="" /><ClinicAd w={40} h={50} tone={2} line="" /><ClinicAd w={30} h={54} tone={1} line="" /></div>
              </div>
            </div>
            <div className="lp-col"><div className="lp-col-h"><span>Scheduled</span><span>2</span></div>
              <div className="lp-job"><Pill tone="info">Scheduled · 3 Nov</Pill><b>Teeth whitening search</b><small>Google Ads · 3 ad groups</small></div>
              <div className="lp-job"><Pill tone="info">Scheduled · 5 Nov</Pill><b>Instagram post · Meet the Chennai team</b></div>
            </div>
          </div>
          <div className="lp-review lp-phone-hide">
            <div className="lp-row"><span className="lp-h5">Year-end check-up reminder</span><Pill tone="brand">Ready for review</Pill><span className="lp-tag" style={{ marginLeft: 'auto' }}>Example</span></div>
            <div className="lp-review-body">
              <figure><ClinicAd w={190} h={190} line="Year-end check-up. Book in 2 minutes." /><figcaption>Instagram feed · 1:1</figcaption></figure>
              <figure><ClinicAd w={152} h={190} tone={2} line="Smiles for the whole family." /><figcaption>Facebook feed · 4:5</figcaption></figure>
              <figure><ClinicAd w={107} h={190} tone={1} line="Check-ups before the year ends." /><figcaption>Stories · 9:16</figcaption></figure>
              <div className="lp-settings">
                {[['Objective', 'Leads'], ['Location', '6 km around each clinic'], ['Dates', '20–30 Nov']].map(([k, v]) => <div key={k}><span>{k}</span><b>{v}</b></div>)}
                <div className="lp-checked"><Check size={14} /> Tracking and landing page checked</div>
                <div className="lp-row lp-row-tight" style={{ marginTop: 8 }}><span className="lp-mini-btn lp-grow">Ask for changes</span><span className="lp-mini-btn lp-mini-btn-brand lp-grow">Approve</span></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── AD STUDIO ───────────────────────────────────────────────────── */}
      <section id="studio" className="lp-lang" ref={langRef}>
        <link rel="stylesheet" href={LANG_FONTS} />
        <div className="lp-lang-bg" aria-hidden="true">
          {langNear && LANG_COLUMNS.map((col, i) => (
            <div key={i} className={i % 2 ? 'lp-lang-col lp-lang-down' : 'lp-lang-col lp-lang-up'}>
              {/* Two identical halves, each far taller than the section, so moving by exactly
                  half loops with no gap; spacing is margin, not gap, so half is exact. */}
              {[0, 1].flatMap((h) => [...col, ...col, ...col].map((name, k) => (
                <img key={`${h}-${k}`} src={LANG_BG(name)} alt="" decoding="async" />
              )))}
            </div>
          ))}
        </div>
        <div className="lp-lang-dim" />
        <div className="lp-wrap lp-lang-copy">
          <Eyebrow onNavy>Ad Studio</Eyebrow>
          <h2 className="lp-h2 lp-on-navy">Ads that look designed.<br />In your customers' language.</h2>
          <p className="lp-sub lp-lang-sub">Every ad here started as one line and, sometimes, a product photo. Your brand kit on each, every size, written like a local wrote it.</p>
          <p className="lp-lang-names">
            {LANG_SAMPLE.map((n) => <span key={n}>{n}</span>)}
            <a href="#faq" onClick={openLanguages}>+40 more</a>
          </p>
          <Link to={TRIAL} className="lp-btn lp-btn-primary lp-btn-lg lp-lang-cta">Make your first ad</Link>
        </div>
      </section>

      {/* ── AFTER LAUNCH ────────────────────────────────────────────────── */}
      <section id="results" className="lp-section lp-white">
        <div className="lp-wrap lp-stack lp-after">
          <div className="lp-after-head">
            <div className="lp-head">
              <Eyebrow>04 · After launch</Eyebrow>
              <h2 className="lp-h2">It keeps working after launch.</h2>
              <p className="lp-sub">Every week it checks how the month is going, adjusts, and carries what worked into the next plan. With your OK.</p>
            </div>
            <div className="lp-after-leads">
              <div className="lp-after-stack">
                <span className="lp-after-card lp-after-card-3" />
                <span className="lp-after-card lp-after-card-2" />
                <div className="lp-after-card lp-after-card-1">
                  <span className="lp-initials" style={{ background: '#FDEBDD', color: '#B4530F' }}>AK</span>
                  <div className="lp-grow"><b>Asha K.</b><small>New lead · 2h ago</small></div>
                  <span className="lp-mini-btn lp-mini-btn-sm">WhatsApp</span>
                </div>
              </div>
              <p className="lp-small lp-muted">Every lead in one place.</p>
            </div>
          </div>

          <figure ref={monthRef} className={monthGo ? 'lp-month lp-month-go' : 'lp-month'}>
            <div className="lp-month-legend">
              <span className="lp-h5">October</span>
              <span><i className="lp-month-band-key" />Expected · likely <span className="lp-mono lp-ink">900–1,300</span> leads</span>
              <span><i className="lp-month-line-key" />What actually happened</span>
              <span className="lp-tag" style={{ marginLeft: 'auto' }}>Example</span>
            </div>
            <div className="lp-month-chart">
              <svg viewBox="0 0 1000 300" preserveAspectRatio="none" role="img" aria-label="October leads: expected 900 to 1,300. The actual line dips below the range in week two, rises back into it after the changes, and ends near the top at 1,284.">
                <line x1="0" y1="280" x2="1000" y2="280" stroke="#E3E6EE" strokeWidth="1" vectorEffect="non-scaling-stroke" />
                {[233, 467, 700].map((x) => <line key={x} x1={x} y1="20" x2={x} y2="280" stroke="#EEF0F5" strokeWidth="1" vectorEffect="non-scaling-stroke" />)}
                <polygon points="0,280 1000,113 1000,39" fill="#FDEBDD" />
                <polyline className="lp-month-line" points="0,280 233,248 467,213 600,171 700,139 933,66 1000,42" fill="none" stroke="#0F1526" strokeWidth="3" strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />
              </svg>
              <span className="lp-month-dot" style={{ left: '46.7%', top: '71%' }} />
              <span className="lp-month-dot" style={{ left: '60%', top: '57%' }} />
              <span className="lp-month-dot lp-month-dot-ok" style={{ left: '100%', top: '14%' }} />
              <div className="lp-pin lp-pin-1" style={{ left: '46.7%', top: '71%' }}><em>Week 2</em> · Budget moved to search</div>
              <div className="lp-pin lp-pin-2" style={{ left: '60%', top: '57%' }}><em>Day 18</em> · Fresh ads for a tiring one</div>
              <div className="lp-pin lp-pin-3 lp-pin-ok" style={{ left: '86%', top: '14%' }}><em>Month end</em> · “Carousels win” goes into November</div>
            </div>
            <div className="lp-month-weeks"><span>Week 1</span><span>Week 2</span><span>Week 3</span><span>Week 4</span></div>
            <ol className="lp-pin-list">
              <li><em>Week 2</em> · Budget moved to search</li>
              <li><em>Day 18</em> · Fresh ads for a tiring one</li>
              <li className="lp-pin-ok"><em>Month end</em> · “Carousels win” goes into November</li>
            </ol>
          </figure>
        </div>
      </section>

      {/* ── WHO ─────────────────────────────────────────────────────────── */}
      <section id="who" className="lp-section">
        <div className="lp-wrap lp-stack lp-scale">
          <div className="lp-head-split">
            <div className="lp-head">
              <Eyebrow>Who it's for</Eyebrow>
              <h2 className="lp-h2">One brand or fifty. Same Admeru.</h2>
            </div>
            <p className="lp-sub">It works the same way for the business itself and for the people who market it.</p>
          </div>
          <ol className="lp-scale-line">
            {SCALE.map((x) => (
              <li key={x.t} id={x.id} className={x.icon === 'agency' ? 'lp-scale-step lp-scale-dark' : 'lp-scale-step'}>
                <span className="lp-scale-node" aria-hidden="true">
                  {x.icon === 'one' && <i className="lp-scale-sq" />}
                  {x.icon === 'many' && <><i className="lp-scale-sq lp-scale-sq-sm" /><i className="lp-scale-sq lp-scale-sq-sm" /><i className="lp-scale-sq lp-scale-sq-sm" /></>}
                  {x.icon === 'team' && <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#F27C28" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="9" cy="8" r="3" /><path d="M3 20c0-3 3-5 6-5s6 2 6 5" /><circle cx="17" cy="9" r="2.5" /><path d="M16 15c3 0 5 1.6 5 4" /></svg>}
                  {x.icon === 'agency' && <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#FFB27A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" /><path d="M3 13h18" /></svg>}
                </span>
                <b>{x.t}</b>
                <span>{x.d}</span>
              </li>
            ))}
          </ol>
          <div className="lp-row lp-scale-ctas">
            <Link to={TRIAL} className="lp-btn lp-btn-primary lp-btn-lg">Start 14-day free trial</Link>
            <a href={MAIL_SALES} className="lp-btn lp-btn-ghost lp-btn-lg">Talk to us about agency plans</a>
          </div>
        </div>
      </section>

      {/* ── TRUST ───────────────────────────────────────────────────────── */}
      <section id="trust" className="lp-section lp-section-sm lp-white">
        <div className="lp-wrap lp-split lp-split-top">
          <div className="lp-head">
            <Eyebrow>Channels and security</Eyebrow>
            <h2 className="lp-h3 lp-ink">Your accounts, your data</h2>
            <p className="lp-small">Admeru runs campaigns in your own ad accounts on Facebook, Instagram, Google Ads and Pinterest. Your assets and leads stay yours.</p>
          </div>
          <div className="lp-trust">
            <div>Assets and leads encrypted</div>
            <div>Not used to train public models</div>
            <Link to="/dpa">Data processing agreement</Link>
            <Link to="/sub-processors">Sub-processor list</Link>
            <Link to="/data-deletion">Data deletion</Link>
            <Link to="/privacy">Privacy policy</Link>
          </div>
        </div>
      </section>

      {/* ── PRICING ─────────────────────────────────────────────────────── */}
      <section id="pricing" className="lp-section">
        <div className="lp-wrap lp-stack">
          <div className="lp-head">
            <Eyebrow>Pricing</Eyebrow>
            <h2 className="lp-h2">Pay for the work, not the hours</h2>
            <p className="lp-sub">Every plan includes the strategy, the monthly plan, production and approvals. Credits pay for what gets made. Ad spend goes to the platforms, inside the caps you set.</p>
          </div>
          <div className="lp-trial-strip"><Check size={20} /><span><b>Start with a 14-day free trial of everything.</b> No credit card. Choose a plan when you're ready, or carry on with the free plan.</span><em>Yearly billing: 2 months free</em></div>
          <div className="lp-plans">
            {plans.map((p) => (
              <div key={p.name} className={p.recommended ? 'lp-plan lp-plan-hot' : 'lp-plan'}>
                <div className="lp-row" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div><div className="lp-h5">{p.display_name}</div><div className="lp-small lp-muted">{p.tagline}</div></div>
                  {p.recommended && <Pill tone="brand">Recommended</Pill>}
                </div>
                <div className="lp-price">{money(p.price[currency].monthly, currency)}<span> / month</span></div>
                <ul>{planBullets(p).map((b) => <li key={b}>{b}</li>)}</ul>
                <Link to={TRIAL} className={p.recommended ? 'lp-btn lp-btn-primary' : 'lp-btn lp-btn-ghost'}>Start free trial</Link>
              </div>
            ))}
          </div>
          <div className="lp-two">
            <div className="lp-card lp-pad"><div className="lp-h5">How credits work</div><p className="lp-small">Making things uses credits: an ad set, a video, a post. Before production starts you see an estimate. If a job would run over, Admeru stops and asks. Extra credit packs are available any time.</p></div>
            <div className="lp-card lp-ent">
              <div className="lp-grow"><div className="lp-h5">Enterprise</div><p className="lp-small">Unlimited businesses, SSO, API and custom credit volume.{enterprise?.price[currency].starting_monthly != null && <> From <span className="lp-mono">{money(enterprise.price[currency].starting_monthly, currency)}</span> a month.</>}</p></div>
              <a href={MAIL_SALES} className="lp-btn lp-btn-ghost">Talk to sales</a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FAQ + CLOSING ───────────────────────────────────────────────── */}
      <section id="faq" className="lp-section">
        <div className="lp-wrap lp-faq">
          <div className="lp-head">
            <Eyebrow>Questions</Eyebrow>
            <h2 className="lp-h2">Before you start</h2>
            <p className="lp-small">Something else? Write to <a href={MAIL_HELLO}>connect@admeru.com</a>.</p>
          </div>
          <div className="lp-faq-list">
            {FAQ.map(([q, a], i) => (
              <div key={q} className="lp-faq-item">
                <button type="button" aria-expanded={faq === i} onClick={() => setFaq((o) => (o === i ? -1 : i))}>
                  <span>{q}</span><span aria-hidden="true">{faq === i ? '−' : '+'}</span>
                </button>
                {faq === i && <p>{a}</p>}
              </div>
            ))}
          </div>
        </div>
        <div className="lp-wrap">
          <div className="lp-closing">
            <div>
              <h2 className="lp-h2">Put a strategy behind every campaign</h2>
              <p>Start with a website. Get the strategy, the first month's plan and campaigns ready for review. 14 days free, no credit card.</p>
            </div>
            <Link to={TRIAL} className="lp-btn lp-btn-dark lp-btn-lg">Start 14-day free trial</Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <footer className="lp-footer">
        <div className="lp-wrap">
          <div className="lp-footer-grid">
            <div className="lp-footer-brand">
              <Logo height={26} onDark to="/" />
              <p>Strategy, plans and finished ads. Every month, ready for your OK.</p>
              <a href={MAIL_HELLO}>connect@admeru.com</a>
            </div>
            <div><b>Product</b><a href="#how">How it works</a><a href="#studio">Ad Studio</a><a href="#agencies">For agencies</a><a href="#pricing">Pricing</a><Link to={TRIAL}>Free trial</Link><Link to={LOGIN}>Sign in</Link></div>
            <div><b>Trust</b><a href="#trust">Security</a><Link to="/dpa">DPA</Link><Link to="/sub-processors">Sub-processors</Link><Link to="/data-deletion">Data deletion</Link></div>
            <div><b>Legal</b><Link to="/privacy">Privacy</Link><Link to="/terms">Terms</Link><Link to="/cookie-policy">Cookies</Link><Link to="/acceptable-use">Acceptable use</Link></div>
            <div><b>Contact</b><a href={MAIL_HELLO}>Support</a><a href={MAIL_SALES}>Sales</a></div>
          </div>
          <div className="lp-footer-bottom"><span>© 2026 Admeru</span><span>Payments in INR and USD</span></div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;

// ── Styles (scoped to .lp, light only) ──────────────────────────────────────

const styles = `
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@600;700;800&family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@500&display=swap');
html { scroll-behavior: smooth; }
.lp { --ink:#12141C; --ink2:#4A5468; --ink3:#646D80; --line:#E3E6EE; --ground:#F3F4F7; --brand:#F27C28; --brand-ink:#B4530F; --brand-soft:#FDEBDD; --navy:#0F1526; --navy2:#1B2440; --onnavy:#B9C0D3;
  --display:'Plus Jakarta Sans',Inter,system-ui,sans-serif; --mono:'JetBrains Mono',ui-monospace,Menlo,monospace;
  background:var(--ground); color:var(--ink); font:400 16px/1.6 Inter,system-ui,-apple-system,'Segoe UI',sans-serif; -webkit-font-smoothing:antialiased; min-height:100vh; overflow-x:hidden; color-scheme:light; }
.lp *, .lp *::before, .lp *::after { box-sizing:border-box; }
.lp a { color:var(--brand-ink); }
.lp a:focus-visible, .lp button:focus-visible { outline:2px solid var(--brand); outline-offset:2px; }
.lp img { max-width:100%; }
.lp-wrap { max-width:1280px; margin:0 auto; padding:0 clamp(16px,4vw,48px); }
.lp-stack { display:flex; flex-direction:column; gap:40px; }
.lp-row { display:flex; gap:12px; flex-wrap:wrap; align-items:center; }
.lp-row-tight { gap:8px; }
.lp-grow { flex:1; min-width:0; }
.lp-muted { color:var(--ink3); font-size:14px; }
.lp-small { font-size:15px; color:var(--ink2); margin:0; }
.lp-mono { font:500 13px var(--mono); }
.lp-orange { color:var(--brand); }
.lp-ink, .lp-h3.lp-ink { color:var(--ink); }

/* type */
.lp-eyebrow { font:700 12px/1 Inter,sans-serif; letter-spacing:.11em; text-transform:uppercase; color:var(--brand-ink); }
.lp-eyebrow-navy { color:#FFB27A; }
.lp-h1 { margin:0; font:800 clamp(38px,4.8vw,68px)/1.03 var(--display); letter-spacing:-.035em; text-wrap:balance; }
.lp-h2 { margin:0; font:800 clamp(30px,3.4vw,48px)/1.08 var(--display); letter-spacing:-.028em; text-wrap:balance; max-width:22ch; }
.lp-h3 { margin:0; font:800 clamp(26px,2.8vw,40px)/1.1 var(--display); letter-spacing:-.025em; color:#FFFFFF; text-wrap:balance; }
.lp-h4 { font:800 26px/1.15 var(--display); letter-spacing:-.02em; }
.lp-h5 { font:700 19px/1.25 var(--display); letter-spacing:-.01em; }
.lp-lead { margin:0; font-size:19px; line-height:1.55; color:var(--ink2); max-width:52ch; }
.lp-sub { margin:0; font-size:18px; color:var(--ink2); max-width:62ch; }
.lp-on-navy { color:#FFFFFF; }
.lp-sub-navy { color:var(--onnavy) !important; }
.lp-head { display:flex; flex-direction:column; gap:16px; min-width:0; }
.lp-head-split { display:flex; gap:48px; flex-wrap:wrap; align-items:flex-end; }
.lp-head-split > .lp-head { flex:1 1 460px; }
.lp-head-split > .lp-sub { flex:1 1 360px; }

/* buttons */
.lp-btn { display:inline-flex; align-items:center; justify-content:center; gap:8px; height:44px; padding:0 20px; border-radius:12px; font:700 15px/1 Inter,sans-serif; text-decoration:none; white-space:nowrap; cursor:pointer; transition:background .15s,border-color .15s; }
.lp-btn-lg { height:52px; padding:0 26px; font-size:16px; }
.lp-btn-primary { background:var(--brand); color:var(--navy) !important; }
.lp-btn-primary:hover { background:#FF8F40; }
.lp-btn-ghost { background:#FFFFFF; color:var(--ink) !important; border:1px solid #D3D8E3; font-weight:600; }
.lp-btn-ghost:hover { border-color:var(--brand); }
.lp-btn-dark { background:var(--navy); color:#FFFFFF !important; }
.lp-btn-dark:hover { background:var(--navy2); }
.lp-btn-navy-ghost { border:1px solid #33405F; color:#FFFFFF !important; font-weight:600; }
.lp-btn-navy-ghost:hover { border-color:#FFB27A; }
.lp-mini-btn { display:inline-flex; align-items:center; justify-content:center; height:40px; padding:0 14px; border-radius:10px; border:1px solid #D3D8E3; background:#FFFFFF; font:600 13.5px Inter,sans-serif; color:var(--ink); }
.lp-mini-btn-dark { background:var(--navy); border-color:var(--navy); color:#FFFFFF; }
.lp-mini-btn-brand { background:var(--brand); border-color:var(--brand); color:var(--navy); font-weight:700; }

/* pills, tags */
.lp-pill { display:inline-flex; align-items:center; align-self:flex-start; font:600 12px Inter,sans-serif; padding:3px 10px; border-radius:999px; white-space:nowrap; }
.lp-pill-ok { background:#E3F3EB; color:#1F8A5B; }
.lp-pill-brand { background:var(--brand-soft); color:var(--brand-ink); }
.lp-pill-info { background:#E8EEFB; color:#2C4FA3; }
.lp-pill-warn { background:#FFF4E5; color:#8A4B00; }
.lp-pill-grey { background:#EEF0F5; color:var(--ink2); }
.lp-tag { font:600 11.5px Inter,sans-serif; padding:2px 9px; border-radius:999px; background:#EEF0F5; color:var(--ink2); }
.lp-tag-navy { background:#26324F; color:var(--onnavy); letter-spacing:0; text-transform:none; }
.lp-ref { padding:3px 10px; border-radius:8px; background:var(--ground); border:1px solid var(--line); color:var(--ink2); font-size:13px; }

/* header */
.lp-header { position:sticky; top:0; z-index:40; background:#FFFFFF; border-bottom:1px solid var(--line); }
.lp-nav { height:72px; display:flex; align-items:center; gap:32px; }
.lp-nav-links { display:flex; gap:4px; flex:1; }
.lp-nav-link { display:inline-flex; align-items:center; gap:6px; padding:10px 14px; border-radius:10px; color:var(--ink) !important; text-decoration:none; font-weight:500; font-size:15px; }
.lp-nav-link:hover { background:var(--ground); }
.lp-nav-signin { color:var(--ink) !important; text-decoration:none; font-weight:500; font-size:15px; }
.lp-menu-btn { display:none; width:44px; height:44px; border-radius:12px; border:1px solid #D3D8E3; background:#FFFFFF; color:var(--ink); align-items:center; justify-content:center; }
.lp-drawer { position:absolute; left:0; right:0; top:100%; background:#FFFFFF; border-bottom:1px solid var(--line); box-shadow:0 24px 40px -20px rgba(15,21,38,.3); padding:8px 16px 20px; display:flex; flex-direction:column; }
.lp-drawer-h { font:700 11px Inter,sans-serif; letter-spacing:.1em; text-transform:uppercase; color:var(--ink3); padding:14px 0 4px; }
.lp-drawer > a:not(.lp-btn) { padding:12px 0; border-bottom:1px solid #EEF0F5; color:var(--ink); text-decoration:none; font-weight:600; }
.lp-drawer > a span { display:block; font-weight:400; font-size:13px; color:var(--ink3); }

/* hero */
.lp-hero { background:#FFFFFF; padding:72px 0; overflow:hidden; border-bottom:1px solid var(--line); }
.lp-hero-grid { display:flex; gap:48px; flex-wrap:wrap; align-items:center; }
.lp-hero-copy { flex:1 1 520px; display:flex; flex-direction:column; gap:24px; min-width:0; }
.lp-note { display:inline-flex; align-items:center; gap:6px; font-size:14px; color:var(--ink3); }
.lp-channels { display:flex; flex-wrap:wrap; align-items:center; gap:8px 18px; font-size:14px; color:var(--ink3); }
.lp-channels-label { flex:0 0 100%; font-size:13px; }
.lp-ch { display:inline-flex; align-items:center; gap:7px; color:var(--ink2); font-weight:600; }
.lp-ch-mark { display:block; flex:0 0 18px; }
.lp-hero-stage { flex:1 1 560px; min-width:0; display:flex; justify-content:center; }
.lp-hero-art { position:relative; width:600px; height:610px; flex:0 0 600px; }
.lp-hero-wash { position:absolute; inset:40px 0 0 40px; border-radius:32px; background:var(--brand-soft); }
.lp-hero-phone { position:absolute; left:210px; top:0; width:236px; background:var(--navy); border-radius:36px; padding:8px; box-shadow:0 40px 80px -30px rgba(15,21,38,.5); animation:lp-rise .7s .1s both; }
.lp-hero-phone img { display:block; width:220px; height:391px; object-fit:cover; border-radius:28px; }
.lp-hero-feed { position:absolute; left:0; top:150px; width:250px; background:#FFFFFF; border-radius:16px; overflow:hidden; box-shadow:0 30px 60px -24px rgba(15,21,38,.45); animation:lp-rise .7s .25s both; }
.lp-hero-feed img { display:block; width:250px; height:250px; object-fit:cover; }
.lp-hero-feed-head { display:flex; align-items:center; gap:8px; padding:10px 12px; font:600 12px Inter,sans-serif; }
.lp-hero-feed-head small { display:block; font-weight:400; font-size:10.5px; color:var(--ink3); }
.lp-avatar { width:24px; height:24px; border-radius:999px; background:var(--ground); border:1px solid var(--line); }
.lp-hero-feed-cta { display:flex; justify-content:space-between; padding:10px 12px; font:600 12px Inter,sans-serif; border-top:1px solid #EEF0F5; }
.lp-hero-feed-cta span { color:var(--ink3); }
.lp-hero-square { position:absolute; right:0; top:200px; width:220px; background:#FFFFFF; border-radius:16px; padding:8px; box-shadow:0 30px 60px -24px rgba(15,21,38,.45); animation:lp-rise .7s .4s both; }
.lp-hero-square img { display:block; width:204px; height:204px; object-fit:cover; border-radius:10px; }
.lp-hero-chips { position:absolute; left:270px; top:446px; width:310px; display:flex; flex-direction:column; gap:8px; }
.lp-chip { background:#FFFFFF; border:1px solid var(--line); border-radius:12px; padding:10px 12px; display:flex; align-items:center; gap:10px; font:600 13px Inter,sans-serif; box-shadow:0 12px 24px -12px rgba(15,21,38,.25); }
.lp-chip em { margin-left:auto; font:500 11px var(--mono); font-style:normal; color:var(--ink3); }
.lp-chip-ok { width:22px; height:22px; border-radius:999px; background:#E3F3EB; display:inline-flex; align-items:center; justify-content:center; }
.lp-chip-dark { background:var(--navy); border-color:var(--navy); color:#FFFFFF; }
.lp-chip-dark b { margin-left:auto; height:28px; padding:0 12px; border-radius:8px; background:var(--brand); color:var(--navy); font:700 12px/28px Inter,sans-serif; }
.lp-chip-1 { animation:lp-in .5s .9s both; } .lp-chip-2 { animation:lp-in .5s 1.3s both; } .lp-chip-3 { animation:lp-in .5s 1.7s both; }
@keyframes lp-rise { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:none; } }
@keyframes lp-in { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:none; } }

/* sections */
.lp-section { padding:120px 0; }
.lp-section-sm { padding:88px 0; }
.lp-white { background:#FFFFFF; border-top:1px solid var(--line); border-bottom:1px solid var(--line); }
.lp-navy { background:var(--navy); color:#FFFFFF; }
.lp-card { background:#FFFFFF; border:1px solid var(--line); border-radius:18px; }
.lp-pad { padding:22px 24px; display:flex; flex-direction:column; gap:8px; }
.lp-ent { padding:22px 24px; display:flex; gap:16px; align-items:center; flex-wrap:wrap; }
.lp-ent .lp-grow { flex:1 1 240px; display:flex; flex-direction:column; gap:8px; }
.lp-panel { background:var(--ground); border:1px solid var(--line); border-radius:22px; padding:24px; display:flex; flex-direction:column; gap:12px; min-width:0; }
.lp-panel-title { display:flex; justify-content:space-between; align-items:baseline; font:700 17px var(--display); }
.lp-split { display:flex; gap:64px; flex-wrap:wrap; align-items:center; }
.lp-split > * { flex:1 1 420px; }
.lp-split-top { align-items:flex-start; }
.lp-two { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); gap:16px; }
.lp-label { font:700 12px Inter,sans-serif; letter-spacing:.08em; text-transform:uppercase; color:var(--ink3); }
.lp-icon { width:44px; height:44px; border-radius:12px; background:var(--ground); border:1px solid var(--line); display:inline-flex; align-items:center; justify-content:center; color:var(--ink); flex:0 0 44px; }
.lp-icon-brand { background:var(--brand-soft); border-color:var(--brand-soft); color:var(--brand-ink); width:40px; height:40px; flex-basis:40px; }
.lp-icon-info { background:#E8EEFB; border-color:#E8EEFB; color:#2C4FA3; width:40px; height:40px; flex-basis:40px; }

/* loop */
.lp-loop { list-style:none; margin:0; padding:0; display:grid; grid-template-columns:repeat(7,minmax(0,1fr)); gap:12px; }
.lp-loop-step { background:#FFFFFF; border:1px solid var(--line); border-radius:18px; padding:20px; display:flex; flex-direction:column; gap:8px; }
.lp-loop-step b { font:700 18px/1.2 var(--display); }
.lp-loop-step > span:last-child { font-size:14px; line-height:1.45; color:var(--ink3); }
.lp-loop-n { font:500 13px var(--mono); color:var(--brand-ink); }
.lp-loop-brand { background:var(--brand-soft); border:1.5px solid var(--brand); }
.lp-loop-brand b, .lp-loop-brand > span:last-child { color:#8A3E0A; }
.lp-loop-navy { background:var(--navy); border-color:var(--navy); }
.lp-loop-navy b { color:#FFFFFF; } .lp-loop-navy > span:last-child { color:var(--onnavy); } .lp-loop-navy .lp-loop-n { color:#FFB27A; }
.lp-loop-back { margin:0 40px; height:40px; border:1.5px dashed #9AA3B5; border-top:0; border-radius:0 0 20px 20px; }
.lp-loop-back-label { display:flex; justify-content:center; align-items:center; gap:8px; margin:-12px auto 0; width:max-content; max-width:100%; padding:0 14px; background:var(--ground); font-size:14px; color:var(--ink3); }
.lp-anytime { display:flex; gap:16px; flex-wrap:wrap; align-items:stretch; }
.lp-anytime-copy { flex:0 1 300px; display:flex; flex-direction:column; gap:8px; justify-content:center; }
.lp-anytime-card { flex:1 1 300px; padding:22px; display:flex; gap:16px; align-items:flex-start; }
.lp-anytime-card b { font:700 18px var(--display); }
.lp-anytime-card p { margin:6px 0 0; font-size:14.5px; color:var(--ink2); }

/* understand */
.lp-url { display:flex; gap:10px; align-items:center; background:var(--ground); border:1px solid var(--line); border-radius:14px; padding:8px 8px 8px 16px; max-width:460px; }
.lp-url span { flex:1; font:500 14px var(--mono); color:var(--ink3); }
.lp-url b { height:40px; padding:0 16px; border-radius:10px; background:var(--brand); color:var(--navy); font:700 14px/40px Inter,sans-serif; white-space:nowrap; }
.lp-fact { background:#FFFFFF; border:1px solid var(--line); border-radius:14px; padding:16px 18px; display:flex; gap:14px; align-items:flex-start; justify-content:space-between; }
.lp-fact b { display:block; font-weight:600; font-size:15px; }
.lp-fact small, .lp-job small { display:block; font-size:13px; color:var(--ink3); margin-top:3px; }
.lp-fact small strong { color:var(--ink2); font-weight:600; }
.lp-fact-check { flex-direction:column; border:1.5px solid var(--brand); }
.lp-fact-row { display:flex; gap:14px; justify-content:space-between; width:100%; }

/* strategy */
.lp-strategy { display:flex; gap:24px; flex-wrap:wrap; align-items:stretch; }
.lp-glance { flex:1 1 300px; background:var(--navy); border-radius:20px; padding:28px; display:flex; flex-direction:column; gap:14px; color:var(--onnavy); font-size:14.5px; }
.lp-glance b { display:block; color:#FFFFFF; font-weight:600; }
.lp-why { flex:2 1 520px; min-width:0; padding:28px; display:flex; flex-direction:column; gap:16px; }
.lp-why p { margin:0; font-size:15.5px; color:var(--ink2); }
.lp-why-box { background:var(--brand-soft); border-radius:14px; padding:16px 18px; font-size:14.5px; color:#4A3112; }
.lp-cal-card { padding:28px; overflow-x:auto; }
.lp-cal { min-width:880px; display:flex; flex-direction:column; gap:12px; }
.lp-cal-top { display:flex; gap:14px; align-items:baseline; }
.lp-cal-days { display:grid; grid-template-columns:repeat(30,minmax(0,1fr)); font:500 11px var(--mono); color:var(--ink3); text-align:center; }
.lp-cal-key { color:var(--brand-ink); font-weight:700; }
.lp-cal-board { position:relative; height:210px; background:var(--ground); border:1px solid var(--line); border-radius:14px; }
.lp-cal-mark { position:absolute; top:0; bottom:0; border-left:2px dashed var(--brand); }
.lp-cal-mark span { position:absolute; top:8px; left:6px; white-space:nowrap; font:600 12px Inter,sans-serif; color:var(--brand-ink); }
.lp-cal-bar { position:absolute; height:34px; border-radius:9px; font:600 13px/34px Inter,sans-serif; padding:0 12px; white-space:nowrap; overflow:hidden; }
.lp-cal-dot { position:absolute; top:184px; width:12px; height:12px; border-radius:999px; background:var(--ink3); }
.lp-legend { display:inline-flex; align-items:center; gap:8px; }
.lp-legend i { display:inline-block; }

/* production */
.lp-board { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:16px; }
.lp-col { background:var(--ground); border:1px solid var(--line); border-radius:18px; padding:18px; display:flex; flex-direction:column; gap:12px; }
.lp-col-h { display:flex; justify-content:space-between; font:700 13px Inter,sans-serif; color:var(--ink); } .lp-col-h span:last-child { color:var(--ink3); }
.lp-job { border:1px solid var(--line); }
.lp-settings > .lp-checked { justify-content:flex-start; }
.lp-checked { display:flex; align-items:center; gap:6px; margin-top:4px; padding-top:10px; border-top:1px solid var(--line); font-size:13px; font-weight:600; color:#1F8A5B; }
.lp-job { background:#FFFFFF; border-radius:14px; padding:14px; color:var(--ink); display:flex; flex-direction:column; gap:8px; }
.lp-job b { font-weight:600; font-size:14.5px; }
.lp-progress { height:6px; border-radius:3px; background:repeating-linear-gradient(-45deg,#F27C28 0 8px,#F9B07A 8px 16px); }
.lp-review { background:var(--ground); border-radius:22px; padding:28px; color:var(--ink); display:flex; flex-direction:column; gap:20px; }
.lp-review-body { display:flex; gap:20px; flex-wrap:wrap; align-items:flex-end; }
.lp-review-body figure { margin:0; display:flex; flex-direction:column; gap:8px; }
.lp-review-body figcaption { font-size:13px; color:var(--ink3); }
.lp-settings { flex:1 1 260px; background:#FFFFFF; border:1px solid var(--line); border-radius:14px; padding:18px; display:flex; flex-direction:column; gap:8px; font-size:14px; color:var(--ink2); }
.lp-settings > div:not(.lp-row) { display:flex; justify-content:space-between; gap:12px; } .lp-settings b { color:var(--ink); font-weight:600; }
.lp-clinic-ad { border-radius:12px; padding:12px; display:flex; flex-direction:column; justify-content:space-between; overflow:hidden; box-shadow:inset 0 0 0 1px rgba(15,21,38,.06); }
.lp-clinic-ad-tag { font:700 9px Inter,sans-serif; letter-spacing:.1em; text-transform:uppercase; }
.lp-clinic-ad-line { font:800 16px/1.15 var(--display); letter-spacing:-.01em; }
.lp-clinic-ad-cta { align-self:flex-start; font:700 10px/1 Inter,sans-serif; padding:6px 10px; border-radius:999px; }
.lp-board .lp-clinic-ad { padding:0; border-radius:8px; } .lp-board .lp-clinic-ad > * { display:none; }

/* languages: the ads drift in tilted columns behind one centred message */
.lp-lang { position:relative; height:680px; overflow:hidden; background:var(--navy); color:#FFFFFF; }
.lp-lang-bg { position:absolute; left:50%; top:-600px; display:grid; grid-template-columns:repeat(6, 260px); column-gap:16px; align-items:start; transform:translateX(-50%) rotate(-8deg); transform-origin:50% 900px; }
.lp-lang-col { display:flex; flex-direction:column; will-change:transform; }
.lp-lang-col img { display:block; width:100%; height:auto; border-radius:12px; margin-bottom:16px; }
.lp-lang-up { animation:lp-drift-up 120s linear infinite; }
.lp-lang-down { animation:lp-drift-down 120s linear infinite; }
@keyframes lp-drift-up { from { transform:translateY(0); } to { transform:translateY(-50%); } }
@keyframes lp-drift-down { from { transform:translateY(-50%); } to { transform:translateY(0); } }
.lp-lang-dim { position:absolute; inset:0; background:radial-gradient(ellipse 42% 46% at 50% 50%, rgba(15,21,38,.78) 0%, rgba(15,21,38,.5) 60%, rgba(15,21,38,.2) 100%); }
.lp-lang-copy { position:relative; height:100%; display:flex; flex-direction:column; align-items:center; justify-content:center; text-align:center; gap:18px; text-shadow:0 2px 24px rgba(15,21,38,.9), 0 1px 3px rgba(15,21,38,.8); }
.lp-lang-copy .lp-h2 { max-width:none; font-size:clamp(34px,4.4vw,60px); }
.lp-lang-sub { color:#D6DCE8 !important; max-width:640px; }
.lp-lang-names { margin:6px 0 0; display:flex; flex-wrap:wrap; justify-content:center; align-items:baseline; gap:6px 22px; font:500 20px/1.6 var(--display), 'Noto Sans Devanagari', 'Noto Sans Tamil', 'Noto Sans Bengali', 'Noto Sans Arabic', 'Noto Sans JP', 'Noto Sans KR', sans-serif; color:#FFFFFF; }
.lp-lang-names a { font:600 16px Inter,system-ui,sans-serif; color:#FFB27A !important; text-decoration:none; }
.lp-lang-names a:hover { text-decoration:underline; }
.lp-lang-cta { margin-top:10px; text-shadow:none; }

/* after launch: the month as a line */
.lp-after { gap:48px; }
.lp-after-head { display:flex; flex-wrap:wrap; justify-content:space-between; align-items:flex-end; gap:40px; }
.lp-after-head > .lp-head { flex:1 1 520px; }
.lp-after-leads { flex:0 1 300px; display:flex; flex-direction:column; gap:10px; }
.lp-after-stack { position:relative; height:116px; }
.lp-after-card { position:absolute; background:#FFFFFF; border:1px solid var(--line); border-radius:12px; }
.lp-after-card-3 { left:26px; right:0; top:0; height:54px; background:var(--ground); }
.lp-after-card-2 { left:13px; right:13px; top:17px; height:54px; box-shadow:0 8px 20px -12px rgba(15,21,38,.25); }
.lp-after-card-1 { left:0; right:26px; top:36px; display:flex; align-items:center; gap:10px; padding:10px 12px; box-shadow:0 12px 28px -14px rgba(15,21,38,.35); }
.lp-after-card-1 b { display:block; font-weight:600; font-size:14px; } .lp-after-card-1 small { font-size:12px; color:var(--ink3); }
.lp-after-card-1 .lp-initials { width:32px; height:32px; flex-basis:32px; line-height:32px; font-size:12px; }
.lp-mini-btn-sm { height:28px; padding:0 10px; font-size:12px; border-radius:999px; }
.lp-month { margin:0; display:flex; flex-direction:column; gap:14px; }
.lp-month-legend { display:flex; flex-wrap:wrap; align-items:baseline; gap:10px 24px; font-size:13px; color:var(--ink3); }
.lp-month-legend > span { display:inline-flex; align-items:center; gap:8px; }
.lp-month-band-key { display:inline-block; width:18px; height:10px; border-radius:3px; background:#FDEBDD; border:1px solid #F7C9A4; }
.lp-month-line-key { display:inline-block; width:18px; height:3px; border-radius:2px; background:var(--navy); }
.lp-month-chart { position:relative; width:100%; aspect-ratio:1000 / 300; }
.lp-month-chart svg { position:absolute; inset:0; width:100%; height:100%; overflow:visible; }
.lp-month-line { clip-path:inset(-20px 100% -20px -20px); }
.lp-month-go .lp-month-line { animation:lp-draw 2.4s .2s ease-out forwards; }
.lp-month-dot, .lp-pin { opacity:0; }
.lp-month-go .lp-month-dot { opacity:1; transition:opacity .3s; }
.lp-month-go .lp-month-dot:nth-of-type(1) { transition-delay:1s; } .lp-month-go .lp-month-dot:nth-of-type(2) { transition-delay:1.5s; } .lp-month-go .lp-month-dot:nth-of-type(3) { transition-delay:2.4s; }
@keyframes lp-draw { to { clip-path:inset(-20px -20px -20px -20px); } }
.lp-month-dot { position:absolute; width:12px; height:12px; margin:-6px 0 0 -6px; border-radius:999px; background:var(--brand); box-shadow:0 0 0 4px #FFFFFF; }
.lp-month-dot-ok { background:#1F8A5B; }
.lp-pin { position:absolute; transform:translate(-50%,-100%); margin-top:-16px; white-space:nowrap; background:var(--navy); color:#FFFFFF; border-radius:10px; padding:8px 12px; font-size:13px; box-shadow:0 10px 24px -12px rgba(15,21,38,.6); }
.lp-month-go .lp-pin { animation:lp-pin .5s both; }
.lp-pin em, .lp-pin-list em { font-style:normal; font-weight:600; color:#FFB27A; }
.lp-pin-ok { background:#FFFFFF; color:var(--ink); border:1px solid #CDE9DA; box-shadow:0 10px 24px -12px rgba(15,21,38,.35); }
.lp-pin-ok em, .lp-pin-list .lp-pin-ok em { color:#1F8A5B; }
.lp-month-go .lp-pin-1 { animation-delay:1s; } .lp-month-go .lp-pin-2 { animation-delay:1.5s; } .lp-month-go .lp-pin-3 { animation-delay:2.4s; }
@keyframes lp-pin { from { opacity:0; transform:translate(-50%,-100%) translateY(8px); } to { opacity:1; transform:translate(-50%,-100%); } }
.lp-month-weeks { display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); font:500 11.5px var(--mono); color:var(--ink3); }
.lp-pin-list { display:none; list-style:none; margin:4px 0 0; padding:0; flex-direction:column; gap:8px; font-size:14px; color:var(--ink2); }
.lp-pin-list em { color:var(--brand-ink); }
.lp-pin-list .lp-pin-ok { background:none; border:0; box-shadow:none; }
.lp-initials { width:36px; height:36px; flex:0 0 36px; border-radius:999px; font:700 13px/36px Inter,sans-serif; text-align:center; }

/* who + agency */
.lp-scale-line { position:relative; list-style:none; margin:0; padding:0; display:grid; grid-template-columns:repeat(4,minmax(0,1fr)); gap:16px; }
.lp-scale-line::before { content:''; position:absolute; left:12.5%; right:12.5%; top:36px; height:2px; background:linear-gradient(90deg,#F7C9A4,#F27C28 70%,#0F1526); }
.lp-scale-step { position:relative; display:flex; flex-direction:column; align-items:center; text-align:center; gap:12px; scroll-margin-top:96px; }
.lp-scale-step b { font:700 18px/1.25 var(--display); }
.lp-scale-step > span:last-child { font-size:14px; color:var(--ink3); max-width:210px; }
.lp-scale-node { width:72px; height:72px; border-radius:999px; background:#FFFFFF; border:2px solid #F4B183; display:flex; align-items:center; justify-content:center; gap:4px; }
.lp-scale-dark .lp-scale-node { background:var(--navy); border-color:var(--navy); }
.lp-scale-sq { display:block; width:18px; height:18px; border-radius:6px; background:var(--brand); }
.lp-scale-sq-sm { width:13px; height:13px; border-radius:4px; }
.lp-scale-ctas { justify-content:center; }

/* trust */
.lp-trust { display:grid; grid-template-columns:repeat(2,minmax(0,1fr)); border-top:1px solid #D3D8E3; }
.lp-trust > * { padding:16px 0; border-bottom:1px solid var(--line); font-size:15px; }
.lp-trust > *:nth-child(even) { padding-left:16px; }

/* pricing */
.lp-trial-strip { display:flex; align-items:center; gap:14px; flex-wrap:wrap; background:#E3F3EB; border-radius:14px; padding:14px 18px; font-size:15px; }
.lp-trial-strip em { margin-left:auto; font-style:normal; font-size:13px; font-weight:600; color:#1F8A5B; }
.lp-plans { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:16px; }
.lp-plan { background:#FFFFFF; border:1px solid var(--line); border-radius:20px; padding:28px; display:flex; flex-direction:column; gap:16px; }
.lp-plan-hot { border:1.5px solid var(--brand); background:#FFFAF6; }
.lp-price { font:500 34px var(--mono); letter-spacing:-.02em; min-height:44px; }
.lp-price span { font:400 14px Inter,sans-serif; color:var(--ink3); letter-spacing:0; }
.lp-plan ul { margin:0; padding-left:18px; font-size:14.5px; color:var(--ink2); display:flex; flex-direction:column; gap:6px; }
.lp-plan .lp-btn { margin-top:auto; }

/* faq + closing */
.lp-faq { display:flex; gap:64px; flex-wrap:wrap; margin-bottom:96px; }
.lp-faq > .lp-head { flex:1 1 320px; }
.lp-faq-list { flex:2 1 560px; min-width:0; border-top:1px solid #D3D8E3; }
.lp-faq-item { border-bottom:1px solid #D3D8E3; }
.lp-faq-item button { width:100%; min-height:64px; display:flex; align-items:center; gap:16px; background:none; border:0; padding:16px 0; text-align:left; font:600 17px/1.4 Inter,sans-serif; color:var(--ink); cursor:pointer; }
.lp-faq-item button span:first-child { flex:1; } .lp-faq-item button span:last-child { font-weight:400; font-size:24px; color:var(--ink3); }
.lp-faq-item p { margin:0; padding:0 48px 20px 0; font-size:15.5px; color:var(--ink2); }
.lp-closing { background:var(--brand); border-radius:28px; padding:clamp(36px,5vw,72px); display:flex; gap:32px; flex-wrap:wrap; align-items:center; color:var(--navy); }
.lp-closing > div { flex:1 1 420px; display:flex; flex-direction:column; gap:12px; }
.lp-closing p { margin:0; font-size:18px; color:#3A2412; }

/* footer */
.lp-footer { background:var(--navy); color:var(--onnavy); padding:72px 0 40px; font-size:14.5px; }
.lp-footer a { color:var(--onnavy); text-decoration:none; } .lp-footer a:hover { color:#FFFFFF; }
.lp-footer-grid { display:flex; gap:48px; flex-wrap:wrap; }
.lp-footer-grid > div { flex:1 1 150px; display:flex; flex-direction:column; gap:10px; }
.lp-footer-grid b { color:#FFFFFF; font-weight:600; }
.lp-footer-grid > .lp-footer-brand { flex:1.4 1 260px; gap:14px; }
.lp-footer-brand p { margin:0; max-width:34ch; }
.lp-footer .lp-footer-brand > a { color:#FFB27A; }
.lp-footer-bottom { margin-top:48px; padding-top:24px; border-top:1px solid var(--navy2); font-size:13px; display:flex; justify-content:space-between; flex-wrap:wrap; gap:12px; }

/* anchors clear the sticky header */
.lp section[id] { scroll-margin-top:72px; }

/* responsive */
@media (max-width:1100px) {
  .lp-loop { grid-template-columns:repeat(4,minmax(0,1fr)); }
}
@media (max-width:900px) {
  .lp-nav { gap:12px; height:64px; }
  .lp-nav-links, .lp-nav-signin { display:none; }
  .lp-nav > .lp-btn { margin-left:auto; height:40px; padding:0 14px; font-size:14px; }
  .lp-menu-btn { display:inline-flex; }
  .lp-hero { padding:40px 0 48px; }
  .lp-section { padding:72px 0; }
  .lp-scale-line { grid-template-columns:repeat(2,minmax(0,1fr)); row-gap:32px; }
  .lp-scale-line::before { display:none; }
  .lp-board, .lp-plans, .lp-two { grid-template-columns:minmax(0,1fr); }
  .lp-hero-art { transform:scale(.6); transform-origin:top center; margin-bottom:-244px; }
  .lp-lang { height:620px; }
  .lp-lang-bg { grid-template-columns:repeat(6, 150px); column-gap:10px; }
  .lp-lang-col img { margin-bottom:10px; }
  .lp-lang-names { font-size:17px; gap:2px 16px; }
}
@media (max-width:640px) {
  .lp-section { padding:56px 0; }
  .lp-pin { display:none; }
  .lp-pin-list { display:flex; }
  .lp-after-leads { flex-basis:100%; }
  .lp-stack { gap:28px; }
  .lp-loop { grid-template-columns:repeat(2,minmax(0,1fr)); gap:8px; }
  .lp-loop-step { padding:14px; border-radius:14px; gap:4px; }
  .lp-loop-step b { font-size:16px; }
  .lp-loop-step > span:last-child { font-size:13px; }
  .lp-loop-step:last-child { grid-column:1 / -1; }
  .lp-faq { margin-bottom:56px; gap:28px; }
  .lp-phone-hide { display:none; }
  .lp-scale-step b { font-size:16px; }
  .lp-scale-node { width:60px; height:60px; }
  .lp-loop-back, .lp-loop-back-label { display:none; }
  .lp-trust { grid-template-columns:minmax(0,1fr); }
  .lp-trust > *:nth-child(even) { padding-left:0; }
  .lp-row > .lp-btn-lg { flex:1 1 100%; }
  .lp-hero-art { transform:scale(.56); margin-bottom:-268px; }
  .lp-review-body figure:nth-child(1), .lp-review-body figure:nth-child(2) { display:none; }
  .lp-faq-item p { padding-right:0; }
}
@media (prefers-reduced-motion: reduce) {
  .lp-month-line { clip-path:none; }
  .lp-month-dot, .lp-pin { opacity:1; }
  .lp *, .lp *::before, .lp *::after { animation:none !important; }
  html { scroll-behavior:auto; }
}
`;
